import React, { useEffect, useMemo, useState } from "react";
import {
  X, Pencil, Trash2, MessageCircle, ListChecks, Megaphone, User,
  ArrowDownLeft, ArrowUpRight, CheckCheck, Check, AlertTriangle, ExternalLink,
  Mail, Phone as PhoneIcon, Building2, MapPin, Briefcase, Tag, Calendar,
} from "lucide-react";
import { toast } from "react-toastify";
import { getContactDetails, deleteContact } from "../../apis/contacts";

/**
 * ContactDetailsDrawer
 *
 * Read-only "view" surface for a single contact. Tabs:
 *   - Overview      identity, address, custom fields, tags, status, last activity
 *   - Activity      recent message timeline (inbound + outbound, with read status)
 *   - Lists         memberships
 *   - Campaigns     campaigns this contact was part of
 *
 * Props:
 *   - phoneId      (required)
 *   - contactId    (required) — id of the contact to load
 *   - onClose      ()
 *   - onEdit       (contact) - parent opens the edit drawer
 *   - onDeleted    () - parent refreshes list after a successful delete
 */
const TABS = [
  { id: "overview",  label: "Overview",  icon: User },
  { id: "activity",  label: "Activity",  icon: MessageCircle },
  { id: "lists",     label: "Lists",     icon: ListChecks },
  { id: "campaigns", label: "Campaigns", icon: Megaphone },
];

const ContactDetailsDrawer = ({ phoneId, contactId, onClose, onEdit, onDeleted }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [tab, setTab] = useState("overview");

  useEffect(() => {
    if (!phoneId || !contactId) return;
    setLoading(true);
    setError("");
    getContactDetails(contactId, phoneId)
      .then((r) => setData(r.data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load contact"))
      .finally(() => setLoading(false));
  }, [phoneId, contactId]);

  const onDelete = async () => {
    if (!data?.contact?.id) return;
    if (!window.confirm("Delete this contact? This cannot be undone.")) return;
    try {
      await deleteContact(data.contact.id, phoneId);
      toast.success("Contact deleted");
      onDeleted && onDeleted();
    } catch (err) {
      toast.error(err.response?.data?.error || "Delete failed");
    }
  };

  const c = data?.contact;
  const initials = useMemo(() => {
    if (!c) return "?";
    const src = c.full_name || c.first_name || c.whatsapp_name || c.phone || "?";
    return src.split(/\s+/).slice(0, 2).map((s) => s[0]).join("").toUpperCase();
  }, [c]);

  return (
    <>
      <div className="fixed inset-0 bg-black/30 z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 bottom-0 w-full max-w-2xl bg-white shadow-2xl z-50 flex flex-col">
        <div className="px-5 py-4 border-b border-gray-200">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3 min-w-0">
              <div className="w-12 h-12 rounded-full bg-emerald-50 text-emerald-700 flex items-center justify-center font-semibold text-base shrink-0">
                {initials}
              </div>
              <div className="min-w-0">
                <h3 className="text-base font-semibold text-gray-900 truncate">
                  {c?.full_name || c?.first_name || c?.whatsapp_name || (loading ? "Loading…" : "Contact")}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5 truncate">
                  {c?.country_code ? `+${c.country_code} ` : ""}{c?.phone || "—"}
                  {c?.email && <span className="ml-2">· {c.email}</span>}
                </p>
              </div>
            </div>
            <button onClick={onClose} className="p-1.5 rounded hover:bg-gray-100">
              <X size={18} />
            </button>
          </div>

          {!loading && c && (
            <div className="mt-3 flex flex-wrap items-center gap-2 text-xs">
              <StatusPill status={c.status} />
              {c.source && (
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-700 capitalize">
                  {String(c.source).replace("_", " ")}
                </span>
              )}
              {c.last_activity_at && (
                <span className="text-gray-500">
                  Last activity {new Date(c.last_activity_at).toLocaleString()}
                </span>
              )}
              <span className="text-gray-400">·</span>
              <span className="text-gray-500">ID #{c.id}</span>
            </div>
          )}
        </div>

        {/* Tabs */}
        <div className="px-5 border-b border-gray-200 bg-gray-50">
          <div className="flex gap-1 -mb-px">
            {TABS.map((t) => {
              const active = tab === t.id;
              const Icon = t.icon;
              const count = badgeForTab(t.id, data);
              return (
                <button
                  key={t.id}
                  onClick={() => setTab(t.id)}
                  className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors whitespace-nowrap ${
                    active
                      ? "border-emerald-600 text-emerald-700"
                      : "border-transparent text-gray-500 hover:text-gray-700"
                  }`}
                >
                  <Icon size={14} />
                  {t.label}
                  {count !== null && (
                    <span className={`ml-0.5 px-1.5 py-0.5 rounded-full text-[10px] ${active ? "bg-emerald-100 text-emerald-700" : "bg-gray-200 text-gray-600"}`}>
                      {count}
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loading ? (
            <p className="text-sm text-gray-400">Loading…</p>
          ) : error ? (
            <div className="bg-rose-50 border border-rose-200 text-rose-700 rounded-md px-3 py-2 text-sm">
              {error}
            </div>
          ) : !c ? (
            <p className="text-sm text-gray-400">No contact data.</p>
          ) : tab === "overview" ? (
            <OverviewTab data={data} />
          ) : tab === "activity" ? (
            <ActivityTab data={data} />
          ) : tab === "lists" ? (
            <ListsTab data={data} />
          ) : tab === "campaigns" ? (
            <CampaignsTab data={data} />
          ) : null}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between">
          {c ? (
            <button
              type="button"
              onClick={onDelete}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-rose-700 border border-rose-200 rounded-md hover:bg-rose-50"
            >
              <Trash2 size={14} /> Delete
            </button>
          ) : <span />}
          <div className="flex gap-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md">
              Close
            </button>
            {c && (
              <button
                onClick={() => onEdit && onEdit(c)}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
              >
                <Pencil size={14} /> Edit
              </button>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

function badgeForTab(id, data) {
  if (!data) return null;
  if (id === "lists") return (data.lists || []).length;
  if (id === "activity") return (data.messages || []).length;
  if (id === "campaigns") return (data.campaigns || []).length;
  return null;
}

const StatusPill = ({ status }) => {
  const map = {
    active:   "bg-emerald-50 text-emerald-700",
    inactive: "bg-gray-100 text-gray-700",
    blocked:  "bg-rose-50 text-rose-700",
  };
  return (
    <span className={`px-2 py-0.5 rounded-full capitalize ${map[status] || "bg-gray-100 text-gray-700"}`}>
      {status || "active"}
    </span>
  );
};

/* ─────────────────────────── Overview ─────────────────────────── */

const OverviewTab = ({ data }) => {
  const c = data.contact;
  const cfDefs = data.custom_field_definitions || [];
  const cfMap = c.custom_fields && typeof c.custom_fields === "object"
    ? c.custom_fields
    : (() => { try { return JSON.parse(c.custom_fields || "{}"); } catch { return {}; } })();
  const tags = Array.isArray(c.tags)
    ? c.tags
    : (() => { try { return JSON.parse(c.tags || "[]"); } catch { return []; } })();

  return (
    <div className="space-y-5">
      {/* Counters */}
      <CountersStrip counts={data.counts} />

      <Section title="Identity" icon={User}>
        <DefList items={[
          ["Phone", c.phone ? `${c.country_code ? `+${c.country_code} ` : ""}${c.phone}` : "—"],
          ["Full name", c.full_name],
          ["First name", c.first_name],
          ["Last name", c.last_name],
          ["Email", c.email],
          ["WhatsApp name", c.whatsapp_name],
        ]} />
      </Section>

      <Section title="Work" icon={Briefcase}>
        <DefList items={[
          ["Company", c.company_name],
          ["Designation", c.designation],
        ]} />
      </Section>

      <Section title="Address" icon={MapPin}>
        <DefList items={[
          ["Address", c.address],
          ["City", c.city],
          ["State", c.state],
          ["Country", c.country],
          ["Pincode", c.pincode],
        ]} />
      </Section>

      <Section title="Personal" icon={Calendar}>
        <DefList items={[
          ["Gender", c.gender],
          ["Date of birth", c.date_of_birth],
        ]} />
      </Section>

      <Section title="Tags" icon={Tag}>
        {tags && tags.length > 0 ? (
          <div className="flex flex-wrap gap-1.5">
            {tags.map((t, i) => (
              <span key={i} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs bg-indigo-50 text-indigo-700">
                {t}
              </span>
            ))}
          </div>
        ) : <Empty text="No tags" />}
      </Section>

      {cfDefs.length > 0 && (
        <Section title="Custom fields">
          <DefList items={cfDefs.map((cf) => [cf.label, cfMap[cf.field_key]])} />
        </Section>
      )}

      <Section title="WhatsApp activity" icon={MessageCircle}>
        <DefList items={[
          ["Last inbound",  c.last_inbound_at  && new Date(c.last_inbound_at).toLocaleString()],
          ["Last outbound", c.last_outbound_at && new Date(c.last_outbound_at).toLocaleString()],
          ["Last read",     c.last_read_at     && new Date(c.last_read_at).toLocaleString()],
          ["Last replied",  c.last_replied_at  && new Date(c.last_replied_at).toLocaleString()],
          ["Unread",        c.unread_count > 0 ? `${c.unread_count} message(s)` : "—"],
        ]} />
      </Section>
    </div>
  );
};

const CountersStrip = ({ counts }) => {
  if (!counts) return null;
  const cells = [
    { label: "Inbound",   value: counts.inbound,   tone: "text-rose-700" },
    { label: "Outbound",  value: counts.outbound,  tone: "text-sky-700" },
    { label: "Delivered", value: counts.delivered, tone: "text-emerald-700" },
    { label: "Read",      value: counts.read,      tone: "text-violet-700" },
    { label: "Failed",    value: counts.failed,    tone: "text-amber-700" },
  ];
  return (
    <div className="grid grid-cols-5 gap-2 text-center">
      {cells.map((c) => (
        <div key={c.label} className="bg-gray-50 border border-gray-200 rounded-md py-2">
          <p className={`text-base font-semibold ${c.tone}`}>{Number(c.value || 0).toLocaleString()}</p>
          <p className="text-[10px] uppercase tracking-wide text-gray-500">{c.label}</p>
        </div>
      ))}
    </div>
  );
};

/* ─────────────────────────── Activity ─────────────────────────── */

const ActivityTab = ({ data }) => {
  const messages = data.messages || [];
  if (messages.length === 0) return <Empty text="No messages yet." />;
  return (
    <ul className="space-y-2">
      {messages.map((m) => <MessageRow key={m.id} m={m} />)}
    </ul>
  );
};

const MessageRow = ({ m }) => {
  const inbound = m.direction === "inbound";
  const Icon = inbound ? ArrowDownLeft : ArrowUpRight;
  return (
    <li className="flex items-start gap-3 p-3 rounded-md bg-gray-50 border border-gray-100">
      <div className={`shrink-0 w-7 h-7 rounded-full flex items-center justify-center ${inbound ? "bg-rose-100 text-rose-700" : "bg-sky-100 text-sky-700"}`}>
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className={`text-xs font-medium ${inbound ? "text-rose-700" : "text-sky-700"} capitalize`}>
            {m.direction}
          </span>
          {m.message_type && (
            <span className="text-[10px] uppercase tracking-wide text-gray-500 px-1.5 py-0.5 rounded bg-white border border-gray-200">
              {m.message_type}
            </span>
          )}
          {!inbound && m.last_status && <DeliveryBadge status={m.last_status} />}
          {m.template_name && (
            <span className="text-[10px] text-violet-700 bg-violet-50 px-1.5 py-0.5 rounded">
              Template: {m.template_name}
            </span>
          )}
          <span className="ml-auto text-[11px] text-gray-500">
            {m.sent_at ? new Date(m.sent_at).toLocaleString() : ""}
          </span>
        </div>
        {m.message_text ? (
          <p className="text-sm text-gray-800 mt-1 whitespace-pre-wrap break-words">
            {m.message_text}
          </p>
        ) : m.media_url ? (
          <p className="text-xs text-gray-500 mt-1 inline-flex items-center gap-1">
            <ExternalLink size={12} /> Media: {m.mime_type || "attachment"}
          </p>
        ) : null}
      </div>
    </li>
  );
};

const DeliveryBadge = ({ status }) => {
  const map = {
    sent:      { label: "sent",      icon: Check,        cls: "text-gray-600" },
    delivered: { label: "delivered", icon: CheckCheck,   cls: "text-emerald-600" },
    read:      { label: "read",      icon: CheckCheck,   cls: "text-violet-600" },
    failed:    { label: "failed",    icon: AlertTriangle,cls: "text-rose-600" },
  };
  const cfg = map[status] || map.sent;
  const Icon = cfg.icon;
  return (
    <span className={`inline-flex items-center gap-0.5 text-[10px] uppercase tracking-wide ${cfg.cls}`}>
      <Icon size={11} /> {cfg.label}
    </span>
  );
};

/* ─────────────────────────── Lists ─────────────────────────── */

const ListsTab = ({ data }) => {
  const lists = data.lists || [];
  if (lists.length === 0) return <Empty text="Not in any list." />;
  return (
    <ul className="space-y-2">
      {lists.map((l) => (
        <li key={l.id} className="flex items-center justify-between p-3 rounded-md border border-gray-200 bg-white">
          <div>
            <p className="text-sm font-medium text-gray-900">{l.name}</p>
            <p className="text-[11px] text-gray-500 capitalize">{l.type} · added {new Date(l.added_at).toLocaleDateString()}</p>
          </div>
          <span className={`px-2 py-0.5 text-xs rounded-full ${l.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-700"}`}>
            {l.status}
          </span>
        </li>
      ))}
    </ul>
  );
};

/* ─────────────────────────── Campaigns ─────────────────────────── */

const CAMPAIGN_STATUS_TONE = {
  sent: "bg-sky-50 text-sky-700",
  delivered: "bg-emerald-50 text-emerald-700",
  read: "bg-violet-50 text-violet-700",
  replied: "bg-emerald-100 text-emerald-800",
  failed: "bg-rose-50 text-rose-700",
  pending: "bg-amber-50 text-amber-700",
  queued: "bg-amber-50 text-amber-700",
};

const CampaignsTab = ({ data }) => {
  const list = data.campaigns || [];
  if (list.length === 0) return <Empty text="No campaign history." />;
  return (
    <ul className="space-y-2">
      {list.map((c) => (
        <li key={`${c.campaign_id}-${c.row_id}`} className="p-3 rounded-md border border-gray-200 bg-white">
          <div className="flex items-center gap-2">
            <Megaphone size={14} className="text-gray-400" />
            <p className="text-sm font-medium text-gray-900 truncate flex-1">
              {c.campaign_name || `Campaign #${c.campaign_id}`}
            </p>
            <span className={`px-2 py-0.5 text-[11px] rounded-full capitalize ${CAMPAIGN_STATUS_TONE[c.status] || "bg-gray-100 text-gray-700"}`}>
              {c.status || "—"}
            </span>
          </div>
          <p className="text-[11px] text-gray-500 mt-1">
            {c.created_at ? new Date(c.created_at).toLocaleString() : "—"}
            {c.scheduled_at && c.scheduled_at !== c.created_at && (
              <> · Scheduled {new Date(c.scheduled_at).toLocaleString()}</>
            )}
          </p>
          {c.error_message && (
            <p className="text-[11px] text-rose-700 mt-1 truncate">
              {c.error_message}
            </p>
          )}
        </li>
      ))}
    </ul>
  );
};

/* ─────────────────────────── Shared ─────────────────────────── */

const Section = ({ title, icon: Icon, children }) => (
  <div>
    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1.5">
      {Icon ? <Icon size={12} /> : null}
      {title}
    </p>
    <div className="bg-white border border-gray-100 rounded-lg p-3">
      {children}
    </div>
  </div>
);

const DefList = ({ items }) => {
  const visible = items.filter(([, v]) => v !== undefined && v !== null && v !== "");
  if (visible.length === 0) return <Empty text="—" />;
  return (
    <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2 text-sm">
      {visible.map(([k, v]) => (
        <div key={k} className="min-w-0">
          <dt className="text-[11px] uppercase tracking-wide text-gray-400">{k}</dt>
          <dd className="text-gray-900 break-words">{String(v)}</dd>
        </div>
      ))}
    </dl>
  );
};

const Empty = ({ text }) => (
  <p className="text-sm text-gray-400">{text}</p>
);

export default ContactDetailsDrawer;
